import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterBar, type FilterOption } from '@/components/shared/FilterBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { SOP_FORCE_REFRESH_EVENT } from '@/utils/sopCache';
import {
  forecastReviewService,
  type ForecastReviewAlert,
  type ForecastReviewAlertApprovePreview,
  type ForecastReviewBulkActionResponse,
  type ForecastReviewBulkApprovePreview,
  type ForecastReviewAlertsComparisonContext,
  type ForecastReviewAlertsImpactSummary,
  type ForecastReviewChartResponse,
  type ForecastReviewClassificationBreakdownResponse,
  type ForecastReviewClassificationBreakdownRow,
  type ForecastReviewComparisonResponse,
  type ForecastReviewComparisonRow,
  type ForecastReviewFilterOptions,
  type ForecastReviewKpis,
  type ForecastReviewModifyPreview,
  type ForecastReviewQueryParams,
} from '@/services/forecastReviewService';
import { ForecastLineChart } from '../components/ForecastLineChart';
import { ForecastKpiCards } from '../components/ForecastKpiCards';
import { AlertsInbox, type AlertInboxStatusFilter } from '../components/AlertsInbox';
import { AlertApproveModal, type AlertApproveModalPhase } from '../components/AlertApproveModal';
import { AlertRejectModal } from '../components/AlertRejectModal';
import {
  AlertReactivateModal,
  type AlertReactivateModalPhase,
} from '../components/AlertReactivateModal';
import { BulkApproveModal, type BulkApproveModalPhase } from '../components/BulkApproveModal';
import { BulkRejectModal, type BulkRejectModalPhase } from '../components/BulkRejectModal';
import { ReplenishmentRefreshProgressModal } from '../components/ReplenishmentRefreshProgressModal';
import { useApproveAlertProgressSteps } from '../hooks/useApproveAlertProgressSteps';
import { useReplenishmentRefreshFlow } from '../hooks/useReplenishmentRefreshFlow';
import { ComparisonTable } from '../components/ComparisonTable';
import { ClassificationBreakdown } from '../components/ClassificationBreakdown';
import {
  ModifyForecastSidebar,
  type ModifyForecastSidebarPhase,
  type ModifyForecastPeriodPayload,
} from '../components/ModifyForecastSidebar';

export function ForecastReviewPage() {
  const { t } = useTranslation();
  const warehouseId = useWarehouseId();
  const { isActionEnabled } = useFeatureVisibility();
  const advancedReviewEnabled = isActionEnabled('forecast.advanced_review', false);
  const modifyManualEnabled =
    advancedReviewEnabled && isActionEnabled('forecast.modify_manual', true);
  const reviewKpisEnabled = isActionEnabled('forecast.review_kpis', false);
  const reviewAlertsInboxEnabled = isActionEnabled('forecast.review_alerts_inbox', false);

  const [viewType, setViewType] = useState<'units' | 'value'>('units');
  const [granularity, setGranularity] = useState<'week' | 'month'>('month');
  const [filterOptions, setFilterOptions] = useState<ForecastReviewFilterOptions | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [activeFilters, setActiveFilters] = useState<Array<{ id: string; label: string; value: string }>>([]);
  const [alertLevel, setAlertLevel] = useState<'product' | 'family' | 'brand' | 'supplier'>('product');
  const [alertStatusFilter, setAlertStatusFilter] = useState<AlertInboxStatusFilter>('open');
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsPageSize, setAlertsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [alertsTotal, setAlertsTotal] = useState(0);

  const [chart, setChart] = useState<ForecastReviewChartResponse | null>(null);
  const [kpis, setKpis] = useState<ForecastReviewKpis | null>(null);
  const [alerts, setAlerts] = useState<ForecastReviewAlert[]>([]);
  const [alertsComparisonContext, setAlertsComparisonContext] =
    useState<ForecastReviewAlertsComparisonContext | null>(null);
  const [alertsImpactSummary, setAlertsImpactSummary] =
    useState<ForecastReviewAlertsImpactSummary | null>(null);
  const [comparison, setComparison] = useState<ForecastReviewComparisonResponse | null>(null);
  const [classificationBreakdown, setClassificationBreakdown] =
    useState<ForecastReviewClassificationBreakdownResponse | null>(null);
  const [comparisonLevel, setComparisonLevel] = useState<'product' | 'family' | 'brand' | 'supplier'>('product');
  const [comparisonPage, setComparisonPage] = useState(1);
  const [comparisonPageSize, setComparisonPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [breakdownDimension, setBreakdownDimension] = useState<
    'abc_quantity' | 'abc_value' | 'xyz_class' | 'adi_cv_class'
  >('abc_quantity');
  const [loadingChart, setLoadingChart] = useState(true);
  const [loadingKpis, setLoadingKpis] = useState(false);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [actionAlertId, setActionAlertId] = useState<number | null>(null);
  const [approveTarget, setApproveTarget] = useState<ForecastReviewAlert | null>(null);
  const [approvePreview, setApprovePreview] = useState<ForecastReviewAlertApprovePreview | null>(null);
  const [approvePreviewLoading, setApprovePreviewLoading] = useState(false);
  const [approvePhase, setApprovePhase] = useState<AlertApproveModalPhase>('confirm');
  const [approveError, setApproveError] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ForecastReviewAlert | null>(null);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState<ForecastReviewAlert | null>(null);
  const [reactivatePhase, setReactivatePhase] = useState<AlertReactivateModalPhase>('confirm');
  const [reactivateError, setReactivateError] = useState<string | null>(null);
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkApprovePreview, setBulkApprovePreview] = useState<ForecastReviewBulkApprovePreview | null>(null);
  const [bulkApprovePreviewLoading, setBulkApprovePreviewLoading] = useState(false);
  const [bulkApprovePhase, setBulkApprovePhase] = useState<BulkApproveModalPhase>('confirm');
  const [bulkApproveResult, setBulkApproveResult] = useState<ForecastReviewBulkActionResponse | null>(null);
  const [bulkApproveError, setBulkApproveError] = useState<string | null>(null);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectPhase, setBulkRejectPhase] = useState<BulkRejectModalPhase>('confirm');
  const [bulkRejectResult, setBulkRejectResult] = useState<ForecastReviewBulkActionResponse | null>(null);
  const [bulkRejectError, setBulkRejectError] = useState<string | null>(null);
  const [bulkRejectApplying, setBulkRejectApplying] = useState(false);
  const [modifyTarget, setModifyTarget] = useState<ForecastReviewComparisonRow | null>(null);
  const [modifyPreview, setModifyPreview] = useState<ForecastReviewModifyPreview | null>(null);
  const [modifyPreviewLoading, setModifyPreviewLoading] = useState(false);
  const [modifyModalGranularity, setModifyModalGranularity] = useState<'week' | 'month'>('month');
  const [modifyPhase, setModifyPhase] = useState<ModifyForecastSidebarPhase>('confirm');
  const [modifyError, setModifyError] = useState<string | null>(null);
  const [modifyingProductId, setModifyingProductId] = useState<number | null>(null);

  const queryParams = useMemo((): ForecastReviewQueryParams => {
    const familyId = filters.family?.[0];
    const subfamilyId = filters.subfamily?.[0];
    const brandId = filters.brand?.[0];
    const supplierId = filters.supplier?.[0];
    const productId = filters.product?.[0];
    const brandCode = filters.brand_code?.[0];
    const abcQuantity = filters.abc_quantity?.[0];
    const abcValue = filters.abc_value?.[0];
    const xyzClass = filters.xyz_class?.[0];
    const adiCvClass = filters.adi_cv_class?.[0];
    const profileCode = filters.profile_code?.[0];

    return {
      warehouse_id: typeof warehouseId === 'number' ? warehouseId : undefined,
      family_id: familyId ? Number(familyId) : undefined,
      subfamily_id: subfamilyId ? Number(subfamilyId) : undefined,
      brand_id: brandId ? Number(brandId) : undefined,
      brand_code: brandCode || undefined,
      supplier_id: supplierId ? Number(supplierId) : undefined,
      product_id: productId ? Number(productId) : undefined,
      granularity,
      view_type: viewType,
      ...(advancedReviewEnabled && abcQuantity ? { abc_quantity: abcQuantity } : {}),
      ...(advancedReviewEnabled && abcValue ? { abc_value: abcValue } : {}),
      ...(advancedReviewEnabled && xyzClass ? { xyz_class: xyzClass } : {}),
      ...(advancedReviewEnabled && adiCvClass ? { adi_cv_class: adiCvClass } : {}),
      ...(advancedReviewEnabled && profileCode ? { profile_code: profileCode } : {}),
    };
  }, [filters, warehouseId, granularity, viewType, advancedReviewEnabled]);

  const alertInboxQueryParams = useMemo(
    () => ({
      ...queryParams,
      level: alertLevel,
      status: alertStatusFilter,
    }),
    [queryParams, alertLevel, alertStatusFilter]
  );

  const bulkActionsRunning =
    bulkApprovePreviewLoading ||
    bulkApprovePhase === 'applying' ||
    bulkRejectApplying;

  const loadFilters = useCallback(async () => {
    try {
      const data = await forecastReviewService.getFilters(queryParams);
      setFilterOptions(data);
    } catch {
      setFilterOptions(null);
    }
  }, [queryParams]);

  const loadMainData = useCallback(async () => {
    setLoadingChart(true);
    if (reviewKpisEnabled) setLoadingKpis(true);
    try {
      const chartPromise = forecastReviewService.getChart(queryParams);
      const kpisPromise = reviewKpisEnabled
        ? forecastReviewService.getKpis(queryParams)
        : Promise.resolve(null);
      const [chartRes, kpisRes] = await Promise.all([chartPromise, kpisPromise]);
      setChart(chartRes);
      setKpis(kpisRes);
    } catch {
      setChart(null);
      setKpis(null);
    } finally {
      setLoadingChart(false);
      setLoadingKpis(false);
    }
  }, [queryParams, reviewKpisEnabled]);

  const loadAlerts = useCallback(async () => {
    if (!reviewAlertsInboxEnabled) {
      setAlerts([]);
      setAlertsComparisonContext(null);
      setAlertsImpactSummary(null);
      setAlertsTotal(0);
      setLoadingAlerts(false);
      return;
    }
    setLoadingAlerts(true);
    try {
      const alertsRes = await forecastReviewService.getAlerts({
        ...queryParams,
        level: alertLevel,
        status: alertStatusFilter,
        page: alertsPage,
        page_size: alertsPageSize,
      });
      setAlerts(alertsRes.results);
      setAlertsComparisonContext(alertsRes.comparison_context ?? null);
      setAlertsImpactSummary(alertsRes.impact_summary ?? null);
      setAlertsTotal(alertsRes.count);
    } catch {
      setAlerts([]);
      setAlertsComparisonContext(null);
      setAlertsImpactSummary(null);
      setAlertsTotal(0);
    } finally {
      setLoadingAlerts(false);
    }
  }, [queryParams, alertLevel, alertStatusFilter, alertsPage, alertsPageSize, reviewAlertsInboxEnabled]);

  const loadComparison = useCallback(async () => {
    if (!advancedReviewEnabled) return;
    setLoadingComparison(true);
    try {
      const res = await forecastReviewService.getComparison({
        ...queryParams,
        level: comparisonLevel,
        page: comparisonPage,
        page_size: comparisonPageSize,
      });
      setComparison(res);
    } catch {
      setComparison(null);
    } finally {
      setLoadingComparison(false);
    }
  }, [
    advancedReviewEnabled,
    queryParams,
    comparisonLevel,
    comparisonPage,
    comparisonPageSize,
    granularity,
  ]);

  const loadBreakdown = useCallback(async () => {
    if (!advancedReviewEnabled) return;
    setLoadingBreakdown(true);
    try {
      const res = await forecastReviewService.getClassificationBreakdown({
        ...queryParams,
        dimension: breakdownDimension,
      });
      setClassificationBreakdown(res);
    } catch {
      setClassificationBreakdown(null);
    } finally {
      setLoadingBreakdown(false);
    }
  }, [advancedReviewEnabled, queryParams, breakdownDimension]);

  const loadData = useCallback(async () => {
    await Promise.all([
      loadMainData(),
      loadAlerts(),
      loadComparison(),
      loadBreakdown(),
    ]);
  }, [loadMainData, loadAlerts, loadComparison, loadBreakdown]);

  const replenishmentFlow = useReplenishmentRefreshFlow({
    warehouseId: typeof warehouseId === 'number' ? warehouseId : null,
  });
  const approveProgress = useApproveAlertProgressSteps();
  const bulkApproveProgress = useApproveAlertProgressSteps();
  const modifyProgress = useApproveAlertProgressSteps();

  useEffect(() => {
    setAlertsPage(1);
  }, [queryParams, alertLevel, alertStatusFilter]);

  useEffect(() => {
    setComparisonPage(1);
  }, [queryParams, comparisonLevel]);

  const alertsTotalPages = useMemo(
    () => Math.max(1, Math.ceil(alertsTotal / alertsPageSize)),
    [alertsTotal, alertsPageSize]
  );

  const handleAlertLevelChange = (level: 'product' | 'family' | 'brand' | 'supplier') => {
    setAlertLevel(level);
    setAlertsPage(1);
  };

  const handleAlertStatusFilterChange = (status: AlertInboxStatusFilter) => {
    setAlertStatusFilter(status);
    setAlertsPage(1);
  };

  const comparisonTotalPages = useMemo(
    () => Math.max(1, comparison?.total_pages ?? 1),
    [comparison]
  );

  useEffect(() => {
    void loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    void loadMainData();
  }, [loadMainData]);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    void loadComparison();
  }, [loadComparison]);

  useEffect(() => {
    void loadBreakdown();
  }, [loadBreakdown]);

  useEffect(() => {
    const onRefresh = () => void loadData();
    window.addEventListener(SOP_FORCE_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(SOP_FORCE_REFRESH_EVENT, onRefresh);
  }, [loadData]);

  const filterBarOptions: FilterOption[] = useMemo(() => {
    if (!filterOptions) return [];
    const base: FilterOption[] = [
      {
        id: 'product',
        name: t('forecastReview.filters.product'),
        options: [],
        loadOptionsRemotely: true,
        multiple: false,
      },
      {
        id: 'family',
        name: t('forecastReview.filters.family'),
        options: filterOptions.families.map((f) => ({ value: f.id, label: f.name })),
      },
      {
        id: 'subfamily',
        name: t('forecastReview.filters.subfamily'),
        options: filterOptions.subfamilies.map((s) => ({ value: s.id, label: s.name })),
      },
      {
        id: 'brand',
        name: t('forecastReview.filters.brand'),
        options: filterOptions.brands.map((b) => ({ value: b.id, label: b.name })),
      },
      {
        id: 'brand_code',
        name: t('forecastReview.filters.brandCode'),
        options: filterOptions.brand_codes.map((c) => ({ value: c, label: c })),
      },
      {
        id: 'supplier',
        name: t('forecastReview.filters.supplier'),
        options: filterOptions.suppliers.map((s) => ({ value: s.id, label: s.name })),
      },
    ];
    if (advancedReviewEnabled) {
      base.push(
        {
          id: 'abc_quantity',
          name: t('forecastReview.advancedFilters.abcQuantity'),
          options: [
            { value: 'A', label: 'A' },
            { value: 'B', label: 'B' },
            { value: 'C', label: 'C' },
          ],
        },
        {
          id: 'abc_value',
          name: t('forecastReview.advancedFilters.abcValue'),
          options: [
            { value: 'A', label: 'A' },
            { value: 'B', label: 'B' },
            { value: 'C', label: 'C' },
          ],
        },
        {
          id: 'xyz_class',
          name: t('forecastReview.advancedFilters.xyzClass'),
          options: [
            { value: 'X', label: 'X' },
            { value: 'Y', label: 'Y' },
            { value: 'Z', label: 'Z' },
          ],
        },
        {
          id: 'adi_cv_class',
          name: t('forecastReview.advancedFilters.adiCvClass'),
          options: [
            { value: 'smooth', label: 'Smooth' },
            { value: 'intermittent', label: 'Intermittent' },
            { value: 'erratic', label: 'Erratic' },
            { value: 'lumpy', label: 'Lumpy' },
          ],
        }
      );
    }
    return base;
  }, [filterOptions, t, advancedReviewEnabled]);

  const fetchProductOptionsForFilter = useCallback(
    async (filterId: string, searchQuery: string) => {
      if (filterId !== 'product') return [];
      return forecastReviewService.getProductFilterSliceOrSearch({
        family: filters.family?.[0] ? Number(filters.family[0]) : undefined,
        brand: filters.brand?.[0] ? Number(filters.brand[0]) : undefined,
        supplier: filters.supplier?.[0] ? Number(filters.supplier[0]) : undefined,
        productQuery: searchQuery,
      });
    },
    [filters.family, filters.brand, filters.supplier]
  );

  const closeApproveModal = useCallback(() => {
    if (approvePhase === 'applying') return;
    approveProgress.resetSteps();
    setApproveTarget(null);
    setApprovePreview(null);
    setApprovePreviewLoading(false);
    setApprovePhase('confirm');
    setApproveError(null);
    setActionAlertId(null);
  }, [approvePhase, approveProgress]);

  const handleRequestApprove = useCallback(
    async (alert: ForecastReviewAlert) => {
      setApproveTarget(alert);
      setApprovePreview(null);
      setApprovePhase('confirm');
      setApproveError(null);
      setApprovePreviewLoading(true);
      setActionAlertId(alert.id);
      try {
        const preview = await forecastReviewService.getApprovePreview(alert.id, queryParams);
        setApprovePreview(preview);
      } catch (error) {
        setApproveError(
          error instanceof Error ? error.message : 'No se pudo cargar el preview de la alerta.'
        );
      } finally {
        setApprovePreviewLoading(false);
      }
    },
    [queryParams]
  );

  const handleConfirmApprove = useCallback(async () => {
    if (!approveTarget) return;
    setApprovePhase('applying');
    setApproveError(null);
    setActionAlertId(approveTarget.id);
    approveProgress.beginApplying();
    try {
      await forecastReviewService.approveAlert(approveTarget.id, queryParams);
      approveProgress.completeApplying();
      setApprovePhase('done');
      await loadData();
    } catch (error) {
      approveProgress.failApplying();
      setApprovePhase('error');
      setApproveError(
        error instanceof Error ? error.message : 'No se pudo aprobar la alerta.'
      );
    } finally {
      setActionAlertId(null);
    }
  }, [approveTarget, queryParams, loadData, approveProgress]);

  const handleRequestReject = useCallback((alert: ForecastReviewAlert) => {
    setRejectTarget(alert);
    setActionAlertId(alert.id);
  }, []);

  const closeRejectModal = useCallback(() => {
    if (rejectLoading) return;
    setRejectTarget(null);
    setActionAlertId(null);
  }, [rejectLoading]);

  const handleConfirmReject = useCallback(async () => {
    if (!rejectTarget) return;
    setRejectLoading(true);
    setActionAlertId(rejectTarget.id);
    try {
      await forecastReviewService.rejectAlert(rejectTarget.id, queryParams);
      setRejectTarget(null);
      await loadData();
    } catch (error) {
      console.error('Error rejecting alert:', error);
    } finally {
      setRejectLoading(false);
      setActionAlertId(null);
    }
  }, [rejectTarget, queryParams, loadData]);

  const handleRequestReactivate = useCallback((alert: ForecastReviewAlert) => {
    setReactivateTarget(alert);
    setReactivatePhase('confirm');
    setReactivateError(null);
    setActionAlertId(alert.id);
  }, []);

  const closeReactivateModal = useCallback(() => {
    if (reactivatePhase === 'applying') return;
    setReactivateTarget(null);
    setReactivatePhase('confirm');
    setReactivateError(null);
    setActionAlertId(null);
  }, [reactivatePhase]);

  const handleConfirmReactivate = useCallback(async () => {
    if (!reactivateTarget) return;
    setReactivatePhase('applying');
    setReactivateError(null);
    setActionAlertId(reactivateTarget.id);
    try {
      await forecastReviewService.reactivateAlert(reactivateTarget.id, queryParams);
      setReactivatePhase('done');
      setAlertStatusFilter('open');
      setAlertsPage(1);
      await loadData();
    } catch (error) {
      setReactivatePhase('error');
      setReactivateError(
        error instanceof Error ? error.message : t('forecastReview.alerts.reactivateModal.errorGeneric')
      );
    } finally {
      setActionAlertId(null);
    }
  }, [reactivateTarget, queryParams, loadData, t]);

  const loadModifyPreview = useCallback(
    async (productId: number, modalGran: 'week' | 'month') => {
      setModifyPreviewLoading(true);
      setModifyError(null);
      try {
        const preview = await forecastReviewService.getModifyPreview(productId, {
          ...queryParams,
          granularity: modalGran,
        });
        setModifyPreview(preview);
      } catch (error) {
        setModifyPreview(null);
        setModifyError(
          error instanceof Error ? error.message : t('forecastReview.modifyForecast.errorGeneric')
        );
      } finally {
        setModifyPreviewLoading(false);
      }
    },
    [queryParams, t]
  );

  const closeModifyModal = useCallback(() => {
    if (modifyPhase === 'applying') return;
    modifyProgress.resetSteps();
    setModifyTarget(null);
    setModifyPreview(null);
    setModifyPreviewLoading(false);
    setModifyPhase('confirm');
    setModifyError(null);
    setModifyingProductId(null);
  }, [modifyPhase, modifyProgress]);

  const handleRequestModifyForecast = useCallback(
    async (row: ForecastReviewComparisonRow) => {
      if (!modifyManualEnabled || comparisonLevel !== 'product') return;
      setModifyTarget(row);
      setModifyPreview(null);
      setModifyPhase('confirm');
      setModifyError(null);
      setModifyModalGranularity(granularity);
      setModifyingProductId(row.element_id);
      await loadModifyPreview(row.element_id, granularity);
    },
    [modifyManualEnabled, comparisonLevel, granularity, loadModifyPreview]
  );

  const handleModifyGranularityChange = useCallback(
    (nextGranularity: 'week' | 'month') => {
      if (!modifyTarget) return;
      setModifyModalGranularity(nextGranularity);
      void loadModifyPreview(modifyTarget.element_id, nextGranularity);
    },
    [modifyTarget, loadModifyPreview]
  );

  const handleConfirmModifyForecast = useCallback(
    async (periods: ModifyForecastPeriodPayload[]) => {
      if (!modifyTarget || periods.length === 0) return;
      setModifyPhase('applying');
      setModifyError(null);
      modifyProgress.beginApplying();
      try {
        await forecastReviewService.applyModifyForecast(
          modifyTarget.element_id,
          { ...queryParams, granularity: modifyModalGranularity },
          periods
        );
        modifyProgress.completeApplying();
        setModifyPhase('done');
        await loadData();
      } catch (error) {
        modifyProgress.failApplying();
        setModifyPhase('error');
        setModifyError(
          error instanceof Error ? error.message : t('forecastReview.modifyForecast.errorGeneric')
        );
      } finally {
        setModifyingProductId(null);
      }
    },
    [modifyTarget, queryParams, modifyModalGranularity, loadData, modifyProgress, t]
  );

  const closeBulkApproveModal = useCallback(() => {
    if (bulkApprovePhase === 'applying') return;
    bulkApproveProgress.resetSteps();
    setBulkApproveOpen(false);
    setBulkApprovePreview(null);
    setBulkApprovePreviewLoading(false);
    setBulkApprovePhase('confirm');
    setBulkApproveResult(null);
    setBulkApproveError(null);
  }, [bulkApprovePhase, bulkApproveProgress]);

  const handleRequestBulkApprove = useCallback(async () => {
    setBulkApproveOpen(true);
    setBulkApprovePreview(null);
    setBulkApprovePhase('confirm');
    setBulkApproveResult(null);
    setBulkApproveError(null);
    setBulkApprovePreviewLoading(true);
    try {
      const preview = await forecastReviewService.getBulkApprovePreview(alertInboxQueryParams);
      setBulkApprovePreview(preview);
    } catch (error) {
      setBulkApproveError(
        error instanceof Error ? error.message : 'No se pudo cargar el preview masivo.'
      );
    } finally {
      setBulkApprovePreviewLoading(false);
    }
  }, [alertInboxQueryParams]);

  const handleConfirmBulkApprove = useCallback(async () => {
    setBulkApprovePhase('applying');
    setBulkApproveError(null);
    bulkApproveProgress.beginApplying();
    try {
      const result = await forecastReviewService.bulkApproveAlerts(alertInboxQueryParams);
      bulkApproveProgress.completeApplying();
      setBulkApproveResult(result);
      setBulkApprovePhase(result.failed_count > 0 && result.succeeded_count === 0 ? 'error' : 'done');
      if (result.failed_count > 0 && result.succeeded_count === 0) {
        setBulkApproveError(
          result.failures[0]?.detail ?? 'No se pudo aprobar ninguna alerta del filtro actual.'
        );
      }
      if (result.succeeded_count > 0) {
        await loadData();
      }
    } catch (error) {
      bulkApproveProgress.failApplying();
      setBulkApprovePhase('error');
      setBulkApproveError(
        error instanceof Error ? error.message : 'No se pudo aprobar las alertas.'
      );
    }
  }, [alertInboxQueryParams, loadData, bulkApproveProgress]);

  const handleRequestBulkReject = useCallback(() => {
    setBulkRejectOpen(true);
    setBulkRejectPhase('confirm');
    setBulkRejectResult(null);
    setBulkRejectError(null);
  }, []);

  const closeBulkRejectModal = useCallback(() => {
    if (bulkRejectApplying) return;
    setBulkRejectOpen(false);
    setBulkRejectPhase('confirm');
    setBulkRejectResult(null);
    setBulkRejectError(null);
  }, [bulkRejectApplying]);

  const handleConfirmBulkReject = useCallback(async () => {
    setBulkRejectApplying(true);
    setBulkRejectPhase('applying');
    setBulkRejectError(null);
    try {
      const result = await forecastReviewService.bulkRejectAlerts(alertInboxQueryParams);
      setBulkRejectResult(result);
      setBulkRejectPhase(result.failed_count > 0 && result.succeeded_count === 0 ? 'error' : 'done');
      if (result.failed_count > 0 && result.succeeded_count === 0) {
        setBulkRejectError(
          result.failures[0]?.detail ?? 'No se pudo rechazar ninguna alerta del filtro actual.'
        );
      }
      if (result.succeeded_count > 0) {
        await loadData();
      }
    } catch (error) {
      setBulkRejectPhase('error');
      setBulkRejectError(
        error instanceof Error ? error.message : 'No se pudo rechazar las alertas.'
      );
    } finally {
      setBulkRejectApplying(false);
    }
  }, [alertInboxQueryParams, loadData]);

  const handleFilterChange = (
    newFilters: Record<string, string[]>,
    meta?: { selectedLabel?: { filterId: string; label: string } }
  ) => {
    const updatedFilters: Record<string, string[]> = { ...filters };
    Object.entries(newFilters).forEach(([key, values]) => {
      if (values.length > 0) updatedFilters[key] = values;
      else delete updatedFilters[key];
    });
    setFilters(updatedFilters);

    if (meta?.selectedLabel) {
      const { filterId, label } = meta.selectedLabel;
      setActiveFilters((prev) => {
        const rest = prev.filter((f) => f.id !== filterId);
        const value = updatedFilters[filterId]?.[0] ?? '';
        if (!value) return rest;
        return [...rest, { id: filterId, label, value }];
      });
      return;
    }

    const clearedIds = Object.entries(newFilters)
      .filter(([, values]) => values.length === 0)
      .map(([key]) => key);
    if (clearedIds.length > 0) {
      setActiveFilters((prev) => prev.filter((f) => !clearedIds.includes(f.id)));
    }
  };

  const handleClearFilter = (filterId: string) => {
    handleFilterChange({ [filterId]: [] });
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setActiveFilters([]);
  };

  const classificationFilterLabels: Record<typeof breakdownDimension, string> = {
    abc_quantity: t('forecastReview.advancedFilters.abcQuantity'),
    abc_value: t('forecastReview.advancedFilters.abcValue'),
    xyz_class: t('forecastReview.advancedFilters.xyzClass'),
    adi_cv_class: t('forecastReview.advancedFilters.adiCvClass'),
  };

  const formatClassificationFilterValue = (classValue: string) => {
    if (classValue === 'unknown') return t('forecastReview.classificationBreakdown.unknownClass');
    if (classValue === 'smooth') return 'Smooth';
    if (classValue === 'intermittent') return 'Intermittent';
    if (classValue === 'erratic') return 'Erratic';
    if (classValue === 'lumpy') return 'Lumpy';
    return classValue;
  };

  const handleApplyClassificationFilter = (row: ForecastReviewClassificationBreakdownRow) => {
    const filterKey = breakdownDimension;
    const classValue = row.class_value;
    const current = filters[filterKey]?.[0];

    if (current === classValue) {
      const next = { ...filters };
      delete next[filterKey];
      setFilters(next);
      setActiveFilters((prev) => prev.filter((f) => f.id !== filterKey));
      return;
    }

    const dimLabel = classificationFilterLabels[filterKey];
    const displayValue = formatClassificationFilterValue(classValue);
    handleFilterChange(
      { ...filters, [filterKey]: [classValue] },
      { selectedLabel: { filterId: filterKey, label: `${dimLabel}: ${displayValue}` } }
    );
  };

  const handleApplyChartFilter = (alert: ForecastReviewAlert) => {
    if (alertLevel === 'product' && alert.product_id) {
      const label = alert.element_name ?? String(alert.product_id);
      handleFilterChange(
        { product: [String(alert.product_id)] },
        { selectedLabel: { filterId: 'product', label } }
      );
    } else if (alertLevel === 'family') {
      const id = alert.family_id ?? alert.element_id;
      if (!id) return;
      const label = alert.element_name ?? String(id);
      handleFilterChange({ family: [String(id)] }, { selectedLabel: { filterId: 'family', label } });
    } else if (alertLevel === 'brand') {
      const id = alert.brand_id ?? alert.element_id;
      if (!id) return;
      const label = alert.element_name ?? String(id);
      handleFilterChange({ brand: [String(id)] }, { selectedLabel: { filterId: 'brand', label } });
    } else if (alertLevel === 'supplier') {
      const id = alert.supplier_id ?? alert.element_id;
      if (!id) return;
      const label = alert.element_name ?? String(id);
      handleFilterChange({ supplier: [String(id)] }, { selectedLabel: { filterId: 'supplier', label } });
    }
  };

  const handleApplyComparisonChartFilter = (row: ForecastReviewComparisonRow) => {
    const id = row.element_id;
    if (!id) return;
    const label = row.element_name ?? String(id);

    if (comparisonLevel === 'product') {
      handleFilterChange(
        { product: [String(id)] },
        { selectedLabel: { filterId: 'product', label } }
      );
    } else if (comparisonLevel === 'family') {
      handleFilterChange({ family: [String(id)] }, { selectedLabel: { filterId: 'family', label } });
    } else if (comparisonLevel === 'brand') {
      handleFilterChange({ brand: [String(id)] }, { selectedLabel: { filterId: 'brand', label } });
    } else if (comparisonLevel === 'supplier') {
      handleFilterChange({ supplier: [String(id)] }, { selectedLabel: { filterId: 'supplier', label } });
    }
  };

  const activeComparisonElementId = useMemo(() => {
    if (comparisonLevel === 'product') return filters.product?.[0] ?? null;
    if (comparisonLevel === 'family') return filters.family?.[0] ?? null;
    if (comparisonLevel === 'brand') return filters.brand?.[0] ?? null;
    if (comparisonLevel === 'supplier') return filters.supplier?.[0] ?? null;
    return null;
  }, [comparisonLevel, filters]);

  const handleDrillDown = (alert: ForecastReviewAlert) => {
    if (alertLevel === 'family') {
      const id = alert.family_id ?? alert.element_id;
      if (!id) return;
      const label = alert.element_name ?? String(id);
      setAlertLevel('product');
      setAlertsPage(1);
      handleFilterChange({ family: [String(id)] }, { selectedLabel: { filterId: 'family', label } });
    } else if (alertLevel === 'brand') {
      const id = alert.brand_id ?? alert.element_id;
      if (!id) return;
      const label = alert.element_name ?? String(id);
      setAlertLevel('product');
      setAlertsPage(1);
      handleFilterChange({ brand: [String(id)] }, { selectedLabel: { filterId: 'brand', label } });
    } else if (alertLevel === 'supplier') {
      const id = alert.supplier_id ?? alert.element_id;
      if (!id) return;
      const label = alert.element_name ?? String(id);
      setAlertLevel('product');
      setAlertsPage(1);
      handleFilterChange({ supplier: [String(id)] }, { selectedLabel: { filterId: 'supplier', label } });
    }
  };

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {filterBarOptions.length > 0 ? (
        <FilterBar
          filters={filterBarOptions}
          onFilterChange={handleFilterChange}
          activeFilters={activeFilters}
          onClearFilter={handleClearFilter}
          onClearAllFilters={handleClearAllFilters}
          fetchOptionsForFilter={fetchProductOptionsForFilter}
        />
      ) : null}

      {reviewKpisEnabled ? (
        <ForecastKpiCards
          kpis={kpis}
          loading={loadingKpis}
          viewType={viewType}
          granularity={granularity}
        />
      ) : null}

      {reviewAlertsInboxEnabled ? (
        <AlertsInbox
          alerts={alerts}
          loading={loadingAlerts}
          level={alertLevel}
          viewType={viewType}
          statusFilter={alertStatusFilter}
          onStatusFilterChange={handleAlertStatusFilterChange}
          comparisonContext={alertsComparisonContext}
          impactSummary={alertsImpactSummary}
          pagination={{
            currentPage: alertsPage,
            totalPages: alertsTotalPages,
            totalCount: alertsTotal,
            pageSize: alertsPageSize,
            onPageChange: setAlertsPage,
            onPageSizeChange: (size) => {
              setAlertsPageSize(size);
              setAlertsPage(1);
            },
          }}
          onLevelChange={handleAlertLevelChange}
          onApprove={handleRequestApprove}
          onReject={handleRequestReject}
          onReactivate={handleRequestReactivate}
          onApplyChartFilter={handleApplyChartFilter}
          onDrillDown={alertLevel !== 'product' ? handleDrillDown : undefined}
          actionAlertId={actionAlertId}
          onBulkApprove={alertStatusFilter === 'open' ? () => void handleRequestBulkApprove() : undefined}
          onBulkReject={alertStatusFilter === 'open' ? handleRequestBulkReject : undefined}
          onRefreshSupply={replenishmentFlow.requestRefresh}
          bulkActionsRunning={bulkActionsRunning}
          refreshSupplyRunning={replenishmentFlow.isRunning}
        />
      ) : null}

      <BulkApproveModal
        open={bulkApproveOpen}
        preview={bulkApprovePreview}
        previewLoading={bulkApprovePreviewLoading}
        phase={bulkApprovePhase}
        applySteps={bulkApproveProgress.steps}
        result={bulkApproveResult}
        errorMessage={bulkApproveError}
        onConfirm={handleConfirmBulkApprove}
        onClose={closeBulkApproveModal}
      />

      <BulkRejectModal
        open={bulkRejectOpen}
        alertCount={alertsImpactSummary?.alert_count ?? 0}
        phase={bulkRejectPhase}
        result={bulkRejectResult}
        errorMessage={bulkRejectError}
        onConfirm={handleConfirmBulkReject}
        onClose={closeBulkRejectModal}
      />

      <ReplenishmentRefreshProgressModal
        open={replenishmentFlow.modalOpen}
        mode={replenishmentFlow.modalMode}
        steps={replenishmentFlow.steps}
        isRunning={replenishmentFlow.isRunning}
        flowComplete={replenishmentFlow.flowComplete}
        partialError={replenishmentFlow.partialError}
        replenishmentSucceeded={replenishmentFlow.replenishmentSucceeded}
        onConfirm={() => void replenishmentFlow.confirmRefresh()}
        onClose={replenishmentFlow.closeModal}
        onRetry={() => void replenishmentFlow.retryReplenishment()}
      />

      <AlertApproveModal
        open={approveTarget != null}
        alert={approveTarget}
        preview={approvePreview}
        previewLoading={approvePreviewLoading}
        phase={approvePhase}
        applySteps={approveProgress.steps}
        errorMessage={approveError}
        onConfirm={handleConfirmApprove}
        onClose={closeApproveModal}
      />

      <AlertRejectModal
        open={rejectTarget != null}
        alert={rejectTarget}
        loading={rejectLoading}
        onConfirm={handleConfirmReject}
        onClose={closeRejectModal}
      />

      <AlertReactivateModal
        open={reactivateTarget != null}
        alert={reactivateTarget}
        phase={reactivatePhase}
        errorMessage={reactivateError}
        onConfirm={handleConfirmReactivate}
        onClose={closeReactivateModal}
      />

      <ModifyForecastSidebar
        open={modifyTarget != null}
        row={modifyTarget}
        preview={modifyPreview}
        previewLoading={modifyPreviewLoading}
        modalGranularity={modifyModalGranularity}
        phase={modifyPhase}
        applySteps={modifyProgress.steps}
        errorMessage={modifyError}
        onModalGranularityChange={handleModifyGranularityChange}
        onConfirm={(periods) => void handleConfirmModifyForecast(periods)}
        onClose={closeModifyModal}
      />

      {advancedReviewEnabled ? (
        <ClassificationBreakdown
          data={classificationBreakdown}
          loading={loadingBreakdown}
          viewType={viewType}
          dimension={breakdownDimension}
          onDimensionChange={setBreakdownDimension}
          activeClassValue={filters[breakdownDimension]?.[0] ?? null}
          onApplyChartFilter={handleApplyClassificationFilter}
        />
      ) : null}

      <ForecastLineChart
        data={chart}
        loading={loadingChart}
        viewType={viewType}
        granularity={granularity}
        onViewTypeChange={setViewType}
        onGranularityChange={setGranularity}
        activeFilters={activeFilters}
        onClearFilter={handleClearFilter}
        onClearAllFilters={handleClearAllFilters}
      />

      {advancedReviewEnabled ? (
        <ComparisonTable
          data={comparison}
          loading={loadingComparison}
          viewType={viewType}
          level={comparisonLevel}
          pagination={{
            currentPage: comparisonPage,
            totalPages: comparisonTotalPages,
            totalCount: comparison?.count ?? 0,
            pageSize: comparisonPageSize,
            onPageChange: setComparisonPage,
            onPageSizeChange: (size) => {
              setComparisonPageSize(size);
              setComparisonPage(1);
            },
          }}
          onLevelChange={setComparisonLevel}
          onApplyChartFilter={handleApplyComparisonChartFilter}
          activeElementId={activeComparisonElementId}
          modifyManualEnabled={modifyManualEnabled}
          onModifyForecast={(row) => void handleRequestModifyForecast(row)}
          modifyingProductId={modifyingProductId}
          warehouseSelected={typeof warehouseId === 'number'}
        />
      ) : null}
    </div>
  );
}
